// src/pages/Users/PassageCeintureDocuments/hooks/usePassageCeintureDocumentsController.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllPassageCeintureDocuments,
  getPublishedPassageCeintureDocuments,
  deletePassageCeintureDocument,
} from "../../../../services/passageCeintureDocumentsServices";

import { getBeltsList } from "../../../../services/beltServices";

import {
  DEFAULT_PASSAGE_DOCUMENTS_STATE,
  PASSAGE_DOCUMENTS_STORAGE_KEY,
  buildPassageDocumentsOptions,
  filterAndSortPassageDocuments,
} from "../utils/passageCeintureDocumentsUtils";

function readStorageState() {
  try {
    const saved = localStorage.getItem(PASSAGE_DOCUMENTS_STORAGE_KEY);

    if (!saved) return DEFAULT_PASSAGE_DOCUMENTS_STATE;

    return {
      ...DEFAULT_PASSAGE_DOCUMENTS_STATE,
      ...JSON.parse(saved),
    };
  } catch {
    return DEFAULT_PASSAGE_DOCUMENTS_STATE;
  }
}

export default function usePassageCeintureDocumentsController(user) {
  const canManage = [1, 3].includes(Number(user?.role_id));

  const [allDocuments, setAllDocuments] = useState([]);
  const [belts, setBelts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [ui, setUi] = useState(readStorageState);
  const [searchInput, setSearchInput] = useState(ui.search || "");

  const persistUi = useCallback((nextState) => {
    setUi(nextState);
    localStorage.setItem(
      PASSAGE_DOCUMENTS_STORAGE_KEY,
      JSON.stringify(nextState)
    );
  }, []);

  const updateUi = useCallback((patch) => {
    setUi((prev) => {
      const next = {
        ...prev,
        ...patch,
        perPage:
          patch.perPage !== undefined
            ? patch.perPage
            : DEFAULT_PASSAGE_DOCUMENTS_STATE.perPage,
      };

      localStorage.setItem(
        PASSAGE_DOCUMENTS_STORAGE_KEY,
        JSON.stringify(next)
      );

      return next;
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setUi((prev) => {
        if (prev.search === searchInput) return prev;

        const next = {
          ...prev,
          search: searchInput,
          perPage: DEFAULT_PASSAGE_DOCUMENTS_STATE.perPage,
        };

        localStorage.setItem(
          PASSAGE_DOCUMENTS_STORAGE_KEY,
          JSON.stringify(next)
        );

        return next;
      });
    }, 250);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const refreshDocuments = useCallback(async () => {
    setLoading(true);

    try {
      const data = canManage
        ? await getAllPassageCeintureDocuments()
        : await getPublishedPassageCeintureDocuments();

      setAllDocuments(Array.isArray(data) ? data.filter(Boolean) : []);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du chargement des documents");
      setAllDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  const refreshBelts = useCallback(async () => {
    try {
      const data = await getBeltsList();
      setBelts(Array.isArray(data) ? data.filter(Boolean) : []);
    } catch (error) {
      console.error("Erreur lors du chargement des ceintures :", error);
      setBelts([]);
    }
  }, []);

  useEffect(() => {
    refreshDocuments();
    refreshBelts();
  }, [refreshDocuments, refreshBelts]);

  const dynamicOptions = useMemo(
  () => buildPassageDocumentsOptions(allDocuments, ui, canManage),
  [allDocuments, ui, canManage]
);

  const filtered = useMemo(
    () => filterAndSortPassageDocuments(allDocuments, ui, canManage),
    [allDocuments, ui, canManage]
  );

  const visible = useMemo(
    () => filtered.slice(0, ui.perPage),
    [filtered, ui.perPage]
  );

  const stats = useMemo(() => {
    const published = allDocuments.filter((doc) => !!doc?.is_published).length;
    const drafts = allDocuments.filter((doc) => !doc?.is_published).length;

    return {
      published,
      drafts,
    };
  }, [allDocuments]);

  const resetFilters = useCallback(() => {
    persistUi(DEFAULT_PASSAGE_DOCUMENTS_STATE);
    setSearchInput(DEFAULT_PASSAGE_DOCUMENTS_STATE.search);
  }, [persistUi]);

  const loadMore = useCallback(() => {
    updateUi({
      perPage: ui.perPage + DEFAULT_PASSAGE_DOCUMENTS_STATE.perPage,
    });
  }, [ui.perPage, updateUi]);

  const deleteDocument = useCallback(
    async (document) => {
      const title = document?.titre || "ce document";

      const ok = window.confirm(`Supprimer le document "${title}" ?`);

      if (!ok) return;

      try {
        await deletePassageCeintureDocument(document.id);
        toast.success("Document supprimé avec succès");
        await refreshDocuments();
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la suppression du document");
      }
    },
    [refreshDocuments]
  );

  return {
    loading,
    allDocuments,
    belts,
    filtered,
    visible,
    ui,
    searchInput,
    setSearchInput,
    dynamicOptions,
    canManage,
    stats,
    updateUi,
    resetFilters,
    loadMore,
    refreshDocuments,
    deleteDocument,
  };
}